import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../Pagination';
import { currencySymbol, fmtDateTime, money, todayISO } from '../../utils/region';
import { payrollApi, PrPnd1Config, PrPnd1Problem, PrPnd1YearBody, PrPnd1YearRow, PrPnd1YearsPage } from '../../utils/api/payroll';
import { Dialog, Foot, Loader, actBtn, emptyLine, field, lbl } from '../CompanyWorkspace/dialog';
import { btn, errBox, note, num, table, td, th } from '../CompanyWorkspace/ui';

// ── Admin › PND.1 by tax year (batch 10 R5, design §2.4 pnd1_config_years) ─────────────────────────
// The PND.1 card above this one holds THE configuration — the one the Tax screens compute with, edited in
// place. A row here is a configuration for ONE tax year, and only payroll reads it (pnd1ConfigFor): a payroll
// run of that year reads the row added last for it, a year without one reads the card above.
// ⛔ Append only, like the table: no row is edited or removed here — to change a year its configuration is
// added again, and to go back the earlier one is added again (the list keeps every one, with who and when).
// ⛔ Save sends only what the server has already said it would take: every change is dry-run (PUT
// ?dryRun=1, nothing written) 300 ms after the last keystroke, and Save stays off while that answer is
// pending or names any problem — a refused PUT pages the admins. The problems are the SERVER's
// (services/pnd1ConfigYears.ts pnd1ConfigProblems), shown under the value they name; this screen has no
// rule of its own about rates, caps or brackets.
// ⛔ Statutory names (PND.1) are written here, outside t(): src/i18n/mt/payroll.ts never translates one.

type TF = (key: string, opts?: Record<string, unknown>) => string;
type BracketIn = { upTo: string; rate: string };
type DeductionIn = { name: string; amount: string };
/** The form as typed: every number a string, so a box can be empty while someone types. */
type Form = { taxYear: string; note: string; expenseRate: string; expenseCap: string; ssoCap: string; brackets: BracketIn[]; deductions: DeductionIn[] };

/** A typed number, or null when the box is empty or holds no number — the server then names it. */
const numOrNull = (s: string): number | null => {
  const v = String(s ?? '').trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
/** What is sent. A bracket's empty "up to" is its open end (null), which the server expects on the last one. */
const toBody = (f: Form): PrPnd1YearBody => ({
  taxYear: numOrNull(f.taxYear), note: f.note.trim(),
  config: {
    expenseRate: numOrNull(f.expenseRate), expenseCap: numOrNull(f.expenseCap), ssoCap: numOrNull(f.ssoCap),
    brackets: f.brackets.map(b => ({ upTo: numOrNull(b.upTo), rate: numOrNull(b.rate) })),
    deductions: f.deductions.map(d => ({ name: d.name.trim(), amount: numOrNull(d.amount) })),
  },
});
const fromConfig = (c: PrPnd1Config): Pick<Form, 'expenseRate' | 'expenseCap' | 'ssoCap' | 'brackets' | 'deductions'> => ({
  expenseRate: String(c.expenseRate), expenseCap: String(c.expenseCap), ssoCap: String(c.ssoCap),
  brackets: (c.brackets || []).map(b => ({ upTo: b.upTo == null ? '' : String(b.upTo), rate: String(b.rate) })),
  deductions: (c.deductions || []).map(d => ({ name: String(d.name || ''), amount: String(d.amount) })),
});
/** A problem's path, the one way this screen spells it: config. dropped, [2] read as .2 (taxYear,
 *  expenseRate, brackets.2.upTo, deductions.0.name …). */
const normPath = (p: string): string => String(p || '').replace(/\[(\d+)\]/g, '.$1').replace(/^config\./, '');
const FIELD_WORDS: Record<string, string> = {
  taxYear: 'prl_year_f_tax_year', note: 'prl_year_f_note', expenseRate: 'prl_year_f_expense_rate', expenseCap: 'prl_year_f_expense_cap', ssoCap: 'prl_year_f_sso_cap',
  upTo: 'prl_year_f_up_to', rate: 'prl_year_f_rate', name: 'prl_year_f_name', amount: 'prl_year_f_amount',
};
/** Where a problem is, in the reader's words: "Tax year", "Bracket 3 · Rate %", "Allowance 1 · Name" … A
 *  path this screen does not know is left unnamed — the problem's own words are still said. */
const whereOf = (t: TF, path: string): string => {
  const [head, idx, leaf] = normPath(path).split('.');
  if (head === 'brackets' || head === 'deductions') {
    const group = idx === undefined || idx === '' ? t(head === 'brackets' ? 'prl_year_brackets' : 'prl_year_deductions')
      : head === 'brackets' ? t('prl_year_bracket_n', { n: Number(idx) + 1 }) : t('prl_year_deduction_n', { n: Number(idx) + 1 });
    return leaf && FIELD_WORDS[leaf] ? `${group} · ${t(FIELD_WORDS[leaf])}` : group;
  }
  return FIELD_WORDS[head] ? t(FIELD_WORDS[head]) : '';
};

const sectionCard: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18 };

// ── The list ────────────────────────────────────────────────────────────────────────────────────────
export default function Pnd1ConfigYears() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PrPnd1YearsPage | null>(null);
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    let alive = true;
    payrollApi.pnd1Years(page)
      .then(d => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) setErr(t('prl_year_load_failed', { error: String(e?.message || e) })); });
    return () => { alive = false; };
  }, [page, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const saved = (row: PrPnd1YearRow) => {
    setAdding(false);
    setNotice(t('prl_year_saved', { year: row.taxYear }));
    setPage(1); setReload(n => n + 1);
  };
  const summary = (c: PrPnd1Config) => {
    const rate = c.expenseRate, cap = money(c.expenseCap, 0), sso = money(c.ssoCap, 0);
    const brackets = (c.brackets || []).length, deductions = (c.deductions || []).length;
    return t('prl_year_summary', { rate, cap, sso, brackets, deductions });
  };
  return (
    <div style={sectionCard} data-section="pnd1-config-years">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <i className="ti ti-calendar-dollar" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>PND.1 · {t('prl_year_title')}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>{t('prl_year_lead')}</div>
        </div>
        <button onClick={() => { setNotice(''); setAdding(true); }} style={{ ...btn, whiteSpace: 'nowrap' }} data-action="add-year">
          <i className="ti ti-plus" aria-hidden /> {t('prl_year_add')}
        </button>
      </div>
      {notice && <div style={{ fontSize: 12.5, color: '#166534', marginBottom: 8 }} data-year-notice><i className="ti ti-circle-check" aria-hidden /> {notice}</div>}
      {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
      {data === null ? (err ? null : <Loader />) : !data.rows.length ? <div style={{ ...emptyLine, padding: 14 }} data-years-empty>{t('prl_year_none')}</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('prl_year_col_year')}</th><th style={th}>{t('prl_year_col_summary')}</th><th style={th}>{t('prl_year_col_note')}</th>
                <th style={th}>{t('prl_year_col_by')}</th><th style={th}>{t('prl_year_col_at')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.id} data-year-row={r.id}>
                  <td style={{ ...num, textAlign: 'left', fontWeight: 700 }} data-year={r.taxYear}>{r.taxYear}</td>
                  <td style={td}>{summary(r.config)}</td>
                  <td style={td}>{r.note || '—'}</td>
                  <td style={td} data-year-by>{r.setByName}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }} data-year-at>{fmtDateTime(r.setAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total > 0 && <Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} />}
      {adding && <YearDialog onClose={() => setAdding(false)} onSaved={saved} t={t} />}
    </div>
  );
}

// ── Adding a tax year's configuration (full screen; the form asks before it is lost) ─────────────────
function YearDialog({ onClose, onSaved, t }: { onClose: () => void; onSaved: (row: PrPnd1YearRow) => void; t: TF }) {
  // The year it opens on is the tenant's current one — a default, not an edit: an untouched window closes
  // without a question, and nothing is checked until something is typed.
  const [form, setForm] = useState<Form>(() => ({ taxYear: todayISO().slice(0, 4), note: '', expenseRate: '', expenseCap: '', ssoCap: '', brackets: [], deductions: [] }));
  // FS-12, the house precedent: the EDIT PATH sets it and nothing else does.
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copying, setCopying] = useState(false);
  const [err, setErr] = useState('');
  // The dry run: `ver` moves with every change; the answer is the one for `checked`. Save waits for both to
  // be the same and the answer to name nothing.
  const [ver, setVer] = useState(0);
  const [checked, setChecked] = useState(-1);
  const [problems, setProblems] = useState<PrPnd1Problem[]>([]);
  const [checkErr, setCheckErr] = useState('');
  // Every change is an updater of the form as it is at that moment (the copy lands after a request).
  const change = (fn: (f: Form) => Form) => { setDirty(true); setForm(fn); setVer(v => v + 1); };
  const set = (k: 'taxYear' | 'note' | 'expenseRate' | 'expenseCap' | 'ssoCap') => (v: string) => change(f => ({ ...f, [k]: v }));
  const setBracket = (i: number, k: keyof BracketIn, v: string) => change(f => ({ ...f, brackets: f.brackets.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const setDeduction = (i: number, k: keyof DeductionIn, v: string) => change(f => ({ ...f, deductions: f.deductions.map((d, j) => (j === i ? { ...d, [k]: v } : d)) }));
  useEffect(() => {
    if (!ver) return undefined;   // nothing typed yet: nothing to ask
    let alive = true;
    const body = toBody(form);
    const h = setTimeout(() => {
      payrollApi.pnd1YearsCheck(body)
        .then(r => { if (alive) { setProblems(Array.isArray(r.problems) ? r.problems : []); setChecked(ver); setCheckErr(''); } })
        .catch((e: any) => { if (alive) setCheckErr(String(e?.message || e)); });
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [ver]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pending = ver > 0 && checked !== ver;
  const ready = ver > 0 && !pending && !checkErr && problems.length === 0;
  /** The global configuration as it is NOW (the card above may have changed since the list was read). */
  const copy = async () => {
    if (copying) return;
    setCopying(true); setErr('');
    try { const d = await payrollApi.pnd1Years(1); change(f => ({ ...f, ...fromConfig(d.global) })); }
    catch (e: any) { setErr(t('prl_year_load_failed', { error: String(e?.message || e) })); }
    finally { setCopying(false); }
  };
  const save = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await payrollApi.pnd1YearsAppend(toBody(form));
      setDirty(false);
      onSaved(r.row);
    } catch (e: any) {
      // A backstop only (the dry run said yes): the server's problems take the place of the stale answer.
      if (e?.payload?.code === 'config_invalid' && Array.isArray(e.payload.problems)) { setProblems(e.payload.problems); setChecked(ver); }
      setErr(e?.payload?.code === 'config_invalid' ? t('prl_year_err_invalid') : String(e?.message || e));
      setBusy(false);
    }
  };
  const paths = new Set(problems.map(p => normPath(p.path)));
  const bad = (path: string): React.CSSProperties => (paths.has(path) ? { border: '1px solid #F3B9B9', background: '#FFF8F8' } : {});
  const numBox = (path: string, value: string, onChange: (v: string) => void, extra: React.CSSProperties = {}, placeholder = '') => (
    <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-path={path}
      style={{ ...field, textAlign: 'right', ...extra, ...bad(path) }} />
  );
  const cur = currencySymbol();
  return (
    <Dialog title={<>PND.1 · {t('prl_year_dlg_title')}</>} onClose={onClose} busy={busy} dirty={dirty} fullscreen
      footer={<Foot onClose={onClose} onSubmit={save} busy={busy} ready={ready} icon="ti ti-device-floppy" label={t('prl_year_save')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 860 }} data-year-dialog>
        <div style={note}>{t('prl_year_dlg_lead')}</div>
        {err && <div style={errBox} data-year-error>{err}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={copy} disabled={copying || busy} style={btn} data-action="copy-global">
            <i className={copying ? 'ti ti-loader' : 'ti ti-copy'} aria-hidden /> {t('prl_year_copy')}
          </button>
          <span style={note}>{t('prl_year_copy_hint')}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          <div><label style={lbl}>{t('prl_year_f_tax_year')}</label>{numBox('taxYear', form.taxYear, set('taxYear'), { textAlign: 'left' })}</div>
          <div><label style={lbl}>{t('prl_year_f_expense_rate')}</label>{numBox('expenseRate', form.expenseRate, set('expenseRate'))}</div>
          <div><label style={lbl}>{t('prl_year_f_expense_cap')} ({cur})</label>{numBox('expenseCap', form.expenseCap, set('expenseCap'))}</div>
          <div><label style={lbl}>{t('prl_year_f_sso_cap')} ({cur})</label>{numBox('ssoCap', form.ssoCap, set('ssoCap'))}</div>
        </div>
        <div>
          <label style={lbl}>{t('prl_year_f_note')}</label>
          <input value={form.note} onChange={e => set('note')(e.target.value)} placeholder={t('prl_year_note_ph')} style={{ ...field, ...bad('note') }} data-path="note" />
        </div>
        {/* the brackets: income up to (empty = no upper limit — the last one) and the rate on that slice */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, ...(paths.has('brackets') ? { borderColor: '#F3B9B9' } : {}) }} data-group="brackets">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, flex: 1 }}>{t('prl_year_brackets')}</div>
            <button type="button" onClick={() => change(f => ({ ...f, brackets: [...f.brackets, { upTo: '', rate: '' }] }))} style={actBtn} data-action="add-bracket">
              <i className="ti ti-plus" aria-hidden /> {t('prl_year_add_bracket')}
            </button>
          </div>
          {!form.brackets.length ? <div style={note}>{t('prl_year_brackets_none')}</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {form.brackets.map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }} data-bracket={i}>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)', width: 82, flex: 'none' }}>{t('prl_year_bracket_n', { n: i + 1 })}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 'none' }}>{t('prl_year_f_up_to')} {cur}</span>
                  {numBox(`brackets.${i}.upTo`, b.upTo, v => setBracket(i, 'upTo', v), { width: 150 }, t('prl_year_up_to_open'))}
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 'none' }}>{t('prl_year_f_rate')}</span>
                  {numBox(`brackets.${i}.rate`, b.rate, v => setBracket(i, 'rate', v), { width: 80 })}
                  <button type="button" onClick={() => change(f => ({ ...f, brackets: f.brackets.filter((_, j) => j !== i) }))} aria-label={t('prl_year_remove')} title={t('prl_year_remove')}
                    style={{ ...actBtn, color: '#B42318', flex: 'none' }}><i className="ti ti-trash" aria-hidden /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* the allowances subtracted from every employee's income (Personal Allowance and the firm's others) */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }} data-group="deductions">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, flex: 1 }}>{t('prl_year_deductions')}</div>
            <button type="button" onClick={() => change(f => ({ ...f, deductions: [...f.deductions, { name: '', amount: '' }] }))} style={actBtn} data-action="add-deduction">
              <i className="ti ti-plus" aria-hidden /> {t('prl_year_add_deduction')}
            </button>
          </div>
          {!form.deductions.length ? <div style={note}>{t('prl_year_deductions_none')}</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {form.deductions.map((d, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }} data-deduction={i}>
                  <input value={d.name} onChange={e => setDeduction(i, 'name', e.target.value)} placeholder={t('prl_year_f_name')} aria-label={t('prl_year_f_name')}
                    style={{ ...field, flex: 1, ...bad(`deductions.${i}.name`) }} data-path={`deductions.${i}.name`} />
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)', flex: 'none' }}>{cur}</span>
                  {numBox(`deductions.${i}.amount`, d.amount, v => setDeduction(i, 'amount', v), { width: 130 })}
                  <button type="button" onClick={() => change(f => ({ ...f, deductions: f.deductions.filter((_, j) => j !== i) }))} aria-label={t('prl_year_remove')} title={t('prl_year_remove')}
                    style={{ ...actBtn, color: '#B42318', flex: 'none' }}><i className="ti ti-trash" aria-hidden /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* the server's answer about what is typed — never a verdict of this screen's own */}
        <div data-year-check={!ver ? 'idle' : pending ? 'pending' : checkErr ? 'error' : problems.length ? 'problems' : 'ok'}>
          {!ver ? null : checkErr ? <div style={errBox}>{t('prl_year_check_failed', { error: checkErr })}</div>
            : pending ? <div style={note}><i className="ti ti-loader" aria-hidden /> {t('prl_year_checking')}</div>
              : problems.length ? (
                <div style={errBox}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('prl_year_problems')}</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {problems.map((p, i) => {
                      const where = whereOf(t, p.path);
                      return <li key={`${p.path}:${p.code}:${i}`} data-problem={p.code} data-problem-path={normPath(p.path)}>{where ? `${where} — ` : ''}{t(`prl_year_problem_${p.code}`)}</li>;
                    })}
                  </ul>
                </div>
              ) : <div style={{ fontSize: 12, color: '#166534' }}><i className="ti ti-circle-check" aria-hidden /> {t('prl_year_ok')}</div>}
        </div>
      </div>
    </Dialog>
  );
}
